/**
 * <ConversationThread> — le fil d'une conversation (organism).
 *
 * Le même fil pour Sam et pour un tuteur : des jours, des salves, des bulles, et ce qui les
 * entoure — les messages plus anciens en tête, « est en train d'écrire » en pied, le retour au
 * dernier message quand on a remonté. Il ne compose que des pièces : `<MessageRow>`,
 * `<TypingIndicator>`, `<Divider>`, `<EmptyState>`, `<Banner>`.
 *
 * Contrôlé : les jours arrivent GROUPÉS (`groupConversation`, `ui/formatters/conversationFormatter`), comme
 * le chargement, l'erreur, la pagination et la frappe de l'autre. Le fil ne garde que sa position
 * de défilement : il suit le bas tant que l'élève y est, et ne le tire pas vers le bas quand il
 * relit plus haut.
 *
 * `autoLoadEarlier` (le web, comme l'ancien fil) : remonter près du haut charge seul les messages
 * précédents, et le fil garde sous les yeux le message qu'on lisait — la page arrivée s'ajoute
 * au-dessus sans le pousser. Le bouton « Voir les messages précédents » reste.
 *
 * Ses états pleins, chacun à la place du fil : le squelette de bulles au premier chargement
 * (jamais une roue), les mots de l'état vide (sans mascotte), l'erreur de lecture et « Réessayer ».
 */
import React, { useEffect, useRef, useState } from "react"
import { ScrollView, View, type NativeScrollEvent, type NativeSyntheticEvent } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { type ChampionName, CHAMPIONS } from "../../atoms/Avatar/ChampionAvatar"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Divider } from "../../atoms/Divider"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { EmptyState } from "../../molecules/EmptyState"
import { type MessageVoiceControls } from "../../molecules/MessageAttachment"
import { type UIMessageLink } from "../../molecules/MessageBubble"
import {
    MessageRow,
    SPEAKER_AVATAR_SIZE,
    type MessageSpeaker,
    type UIConversationMessage,
} from "../../molecules/MessageRow"
import { TypingIndicator } from "../../molecules/TypingIndicator"
import { shadowStyle } from "../../utils/shadow"
import { dsScrollProps } from "../../utils/webScrollbars"
import {
    type UIConversationBurst,
    type UIConversationDay,
    type UIVoicePlayback,
} from "../../../ports/primary/ui/types/studentConversation"
import { ConversationThreadSkeleton } from "./ConversationThreadSkeleton"
import { anchoredOffset, arrivedAnchor, earlierAnchorFor, isAnchored, type EarlierAnchor } from "./earlierAnchor"

/** À qui l'élève parle. À remonter dans `ports/primary/ui/types` au câblage. */
export type UIConversationInterlocutor = {
    /** `agent` : il parle en bulle, sa tête à côté · `human` : il est cité, avec sa photo. */
    speaker: MessageSpeaker
    /** « Sam », « Camille R. » — le nom de l'humain se lit en tête de chacune de ses salves. */
    name: string
    /**
     * L'agent est l'un des quatre (Sam) : le fil pose sa tête lui-même, `<ChampionAvatar>`. Sans
     * effet sur un humain. Absent : `avatar`, comme avant.
     */
    champion?: ChampionName
    /** La capsule de l'humain (`<Avatar variant="tutor">`), `SPEAKER_AVATAR_SIZE` au plus. */
    avatar?: React.ReactNode
}

/** Sam, l'agent de toutes les conversations de l'élève hors tuteur. */
export const SAM_INTERLOCUTOR: UIConversationInterlocutor = {
    speaker: "agent",
    name: CHAMPIONS.sam.label,
    champion: "sam",
}

/**
 * Qui parle dans une salve : l'interlocuteur du fil, ou, dans un fil à plusieurs (« Tous » du live),
 * l'élève qui l'a écrite (`message.sender`) — son visage seul, sans son nom : on ne nomme pas un
 * autre élève. Le tuteur, ou une salve sans auteur, garde l'interlocuteur.
 */
function burstSpeaker(
    burst: UIConversationBurst,
    interlocutor: UIConversationInterlocutor
): UIConversationInterlocutor {
    const sender = burst.author === "other" ? burst.messages[0]?.sender : undefined
    if (sender == null || sender.tutor === true) return interlocutor
    return { speaker: "human", name: "", avatar: <Avatar uri={sender.avatarUrl} size={SPEAKER_AVATAR_SIZE} /> }
}

// Le message vocal en cours d'écoute : sa forme vit dans `ports/primary/ui/types` (câblage de la messagerie).
export type { UIVoicePlayback }

export type ConversationThreadProps = {
    days: UIConversationDay[]
    interlocutor: UIConversationInterlocutor

    /** Premier chargement : le squelette de bulles tient la place du fil. */
    loading?: boolean
    /** La lecture du fil a échoué : la phrase, et `onRetryLoad` pour « Réessayer ». */
    error?: string | null
    onRetryLoad?: () => void
    /** Les mots du fil vide. */
    emptyTitle?: string
    emptyMessage?: string
    /** Ce qui remplace les mots du fil vide — l'accueil de Sam, que son écran compose. */
    empty?: React.ReactNode

    /** Il reste des messages plus anciens : le fil propose de les charger, en tête. */
    hasEarlier?: boolean
    loadingEarlier?: boolean
    onLoadEarlier?: () => void
    /** Remonter près du haut charge seul les messages précédents (le web, comme l'ancien fil). Défaut `false`. */
    autoLoadEarlier?: boolean

    /** L'autre écrit : ce qui se lit sous ses points — « Sam réfléchit… ». Absent, pas de points. */
    typingLabel?: string
    /** Ce qui suit le dernier message, dans le fil — une attente nommée, un bandeau. */
    footer?: React.ReactNode

    onRetryMessage?: (message: UIConversationMessage) => void
    onOpenAttachment?: (message: UIConversationMessage) => void
    /** Appui long sur un message qui a du texte — l'écran ouvre « Copier ». */
    onMessageLongPress?: (message: UIConversationMessage) => void
    voicePlayback?: UIVoicePlayback | null
    onToggleVoice?: (message: UIConversationMessage) => void
    /** Le vocal chargé se règle : sa position (de 0 à 1) et sa vitesse. Absents : lecture seule. */
    onSeekVoice?: (message: UIConversationMessage, ratio: number) => void
    onCycleVoiceRate?: (message: UIConversationMessage) => void
    /** Un lien d'un message touché : l'écran l'ouvre. Absent, les textes restent simples. */
    onPressLink?: (link: UIMessageLink) => void
    testID?: string
}

const TEXTS = {
    emptyTitle: "Aucun message pour l'instant",
    earlier: "Voir les messages précédents",
    retry: "Réessayer",
    toEnd: "Aller au dernier message",
}

/** Au-delà, le fil ne s'élargit plus : sur un grand écran il reste une colonne de lecture. */
export const CONVERSATION_MAX_WIDTH = 720
/** En deçà de cette distance du bas, l'élève « est en bas » : le fil suit les nouveaux messages. */
const END_THRESHOLD = 48
const TO_END_SIZE = 44

export function ConversationThread(props: ConversationThreadProps) {
    const {
        days,
        interlocutor,
        loading = false,
        error,
        onRetryLoad,
        emptyTitle = TEXTS.emptyTitle,
        emptyMessage,
        empty,
        hasEarlier = false,
        loadingEarlier = false,
        onLoadEarlier,
        autoLoadEarlier = false,
        typingLabel,
        footer,
        onRetryMessage,
        onOpenAttachment,
        onMessageLongPress,
        voicePlayback,
        onToggleVoice,
        onSeekVoice,
        onCycleVoiceRate,
        onPressLink,
        testID,
    } = props

    const scroll = useRef<ScrollView>(null)
    // Le fil suit le bas tant que l'élève y est ; une référence, pas un état : elle ne peint rien.
    const follows = useRef(true)
    const [atEnd, setAtEnd] = useState(true)

    /** La page précédente demandée en remontant : tant qu'elle tient, le fil garde le message lu. */
    const anchor = useRef<EarlierAnchor | null>(null)

    // La page est arrivée : le fil se mesure encore un instant, puis lâche l'ancre.
    useEffect(() => {
        if (loadingEarlier || anchor.current == null || anchor.current.arrived) return
        anchor.current = arrivedAnchor(anchor.current, Date.now())
    }, [loadingEarlier])

    const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent
        const end = contentSize.height - layoutMeasurement.height - contentOffset.y < END_THRESHOLD
        follows.current = end
        setAtEnd((previous) => (previous === end ? previous : end))

        if (!autoLoadEarlier || !hasEarlier || loadingEarlier || onLoadEarlier == null) return
        const next = earlierAnchorFor(
            { offset: contentOffset.y, viewport: layoutMeasurement.height, content: contentSize.height },
            anchor.current,
            Date.now()
        )
        if (next == null) return
        anchor.current = next
        onLoadEarlier()
    }

    const onContentSizeChange = (_width: number, height: number) => {
        const held = anchor.current
        if (isAnchored(held, Date.now())) {
            scroll.current?.scrollTo({ y: anchoredOffset(held, height), animated: false })
            return
        }
        anchor.current = null
        // Un fil vide n'a pas de dernier message : son accueil se lit depuis le haut.
        if (follows.current && !isEmpty) followEnd()
    }

    const toEnd = (animated: boolean) => scroll.current?.scrollToEnd({ animated })

    /*
     * Le bas, tout de suite, puis encore une fois la vue native à jour. Sur Android, la mesure du
     * contenu arrive avant que la vue native ne connaisse sa nouvelle hauteur : `scrollToEnd` lancé
     * là s'arrête à l'ancienne fin. À la première ouverture du chat du live, le fil passait de l'accueil
     * vide à tout l'historique d'un coup, et restait sur les messages d'avril.
     */
    const frame = useRef<number | null>(null)
    const followEnd = () => {
        toEnd(false)
        if (frame.current != null) cancelAnimationFrame(frame.current)
        frame.current = requestAnimationFrame(() => {
            frame.current = requestAnimationFrame(() => {
                frame.current = null
                if (follows.current) toEnd(false)
            })
        })
    }
    useEffect(
        () => () => {
            if (frame.current != null) cancelAnimationFrame(frame.current)
        },
        []
    )

    const isTyping = typingLabel != null && typingLabel !== ""
    const hasError = error != null && error !== ""
    const isEmpty = days.length === 0 && !isTyping && footer == null

    let body: React.ReactNode
    if (loading) {
        body = (
            <DelayedSkeleton>
                <ConversationThreadSkeleton />
            </DelayedSkeleton>
        )
    } else if (hasError) {
        body = (
            <View style={styles.state}>
                <Banner message={error} color="danger" icon="exclamation-circle" tinted />
                {onRetryLoad != null && <Button label={TEXTS.retry} onPress={onRetryLoad} />}
            </View>
        )
    } else if (isEmpty) {
        body = empty ?? (
            <View style={styles.empty}>
                <EmptyState title={emptyTitle} message={emptyMessage} />
            </View>
        )
    } else {
        body = (
            <>
                {hasEarlier && loadingEarlier && (
                    <DelayedSkeleton>
                        <ConversationThreadSkeleton variant="earlier" />
                    </DelayedSkeleton>
                )}
                {hasEarlier && !loadingEarlier && onLoadEarlier != null && (
                    <View style={styles.earlier}>
                        <Button label={TEXTS.earlier} variant="ghost" onPress={onLoadEarlier} />
                    </View>
                )}

                {days.map((day) => (
                    <View key={day.id} style={styles.day}>
                        {day.label != null && <Divider label={day.label} spacing="0" />}
                        {day.bursts.map((burst) => {
                            const speaker = burstSpeaker(burst, interlocutor)
                            return (
                                <View key={burst.id} style={styles.burst}>
                                    {burst.messages.map((message, index) => {
                                        const last = index === burst.messages.length - 1
                                        const hasText = message.text != null && message.text !== ""
                                        const playback = voicePlayback?.messageId === message.id ? voicePlayback : null
                                        return (
                                            <MessageRow
                                                key={message.id}
                                                message={message}
                                                speaker={speaker.speaker}
                                                head={index === 0}
                                                tail={last}
                                                champion={speaker.champion}
                                                avatar={speaker.avatar}
                                                authorName={speaker.name}
                                                caption={last ? burst.timeLabel : undefined}
                                                onRetry={
                                                    onRetryMessage != null ? () => onRetryMessage(message) : undefined
                                                }
                                                onOpenAttachment={
                                                    onOpenAttachment != null
                                                        ? () => onOpenAttachment(message)
                                                        : undefined
                                                }
                                                onLongPress={
                                                    onMessageLongPress != null && hasText
                                                        ? () => onMessageLongPress(message)
                                                        : undefined
                                                }
                                                voicePlaying={playback?.playing}
                                                voiceProgress={playback?.progress}
                                                onToggleVoice={
                                                    onToggleVoice != null ? () => onToggleVoice(message) : undefined
                                                }
                                                voiceControls={voiceControlsOf(
                                                    message,
                                                    playback,
                                                    onSeekVoice,
                                                    onCycleVoiceRate
                                                )}
                                                onPressLink={onPressLink}
                                            />
                                        )
                                    })}
                                </View>
                            )
                        })}
                    </View>
                ))}

                {isTyping && (
                    <TypingIndicator
                        label={typingLabel}
                        speaker={interlocutor.speaker}
                        champion={interlocutor.champion}
                        avatar={interlocutor.avatar}
                    />
                )}
                {footer}
            </>
        )
    }

    return (
        <View style={styles.root} testID={testID}>
            <ScrollView
                {...dsScrollProps()}
                ref={scroll}
                style={styles.scroll}
                contentContainerStyle={styles.content}
                onScroll={onScroll}
                scrollEventThrottle={100}
                onContentSizeChange={onContentSizeChange}
                keyboardShouldPersistTaps="handled"
            >
                <View style={styles.column}>{body}</View>
            </ScrollView>

            {!atEnd && !isEmpty && (
                <View style={styles.toEnd}>
                    <IconButton
                        iconName="chevron-down"
                        size={TO_END_SIZE}
                        color="surface-floating"
                        iconColor="text-primary"
                        onPress={() => toEnd(true)}
                        accessibilityLabel={TEXTS.toEnd}
                    />
                </View>
            )}
        </View>
    )
}

/** Le vocal en cours, une fois chargé, se règle ; tout autre vocal reste en lecture seule. */
function voiceControlsOf(
    message: UIConversationMessage,
    playback: UIVoicePlayback | null,
    onSeek: ConversationThreadProps["onSeekVoice"],
    onCycleRate: ConversationThreadProps["onCycleVoiceRate"]
): MessageVoiceControls | undefined {
    if (playback?.seekable !== true || onSeek == null || onCycleRate == null) return undefined
    return {
        rate: playback.rate ?? 1,
        elapsedLabel: playback.elapsedLabel,
        onSeek: (ratio) => onSeek(message, ratio),
        onCycleRate: () => onCycleRate(message),
    }
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0 },
    scroll: { flex: 1 },
    /** `flexGrow` : un fil court, son état vide ou son erreur occupent toute la hauteur. */
    content: {
        flexGrow: 1,
        paddingHorizontal: theme.spacing["4"],
        paddingVertical: theme.spacing["4"],
    },
    column: {
        flexGrow: 1,
        width: "100%",
        maxWidth: CONVERSATION_MAX_WIDTH,
        alignSelf: "center",
        gap: theme.spacing["4"],
    },
    day: { gap: theme.spacing["4"] },
    /** Les bulles d'une même salve se serrent. */
    burst: { gap: theme.spacing["1"] },
    earlier: { alignItems: "center" },
    state: { gap: theme.spacing["4"], alignItems: "flex-start" },
    /** Les mots du fil vide se lisent au milieu de la place que le fil occupera. */
    empty: { flexGrow: 1, justifyContent: "center" },
    /** Le retour au dernier message flotte sur le fil : il porte l'ombre d'une surface flottante. */
    toEnd: {
        position: "absolute",
        right: theme.spacing["4"],
        bottom: theme.spacing["3"],
        borderRadius: TO_END_SIZE / 2,
        ...shadowStyle(theme, "floating"),
    },
}))
